import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save, Upload } from "lucide-react";
import { toast } from "sonner";
import PrescriptionPreview from "@/components/PrescriptionPreview";

function NumberField({ label, value, onChange, testid, suffix = "mm" }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="relative">
        <Input
          type="number"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
          data-testid={testid}
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">{suffix}</span>
      </div>
    </div>
  );
}

const SAMPLE_PRESCRIPTION = {
  prescription_number: "RX-PREVIEW",
  patient_snapshot: { full_name: "Sample Patient", address: "123 Main St., Quezon City", age: 35, gender: "Male" },
  doctor_snapshot: { name: "Dr. Maria Santos, MD", license_number: "LIC-0123456", ptr_number: "PTR-7890123" },
  date: new Date().toISOString(),
  medicines: [
    { medicine_name: "Amoxicillin 500mg", dosage: "1 capsule", frequency: "Three times daily", duration: "7 days", instruction: "After meals" },
    { medicine_name: "Paracetamol 500mg", dosage: "1 tablet", frequency: "Every 6 hours", duration: "As needed", instruction: "" },
  ],
  general_instruction: "Drink plenty of water. Follow up after 1 week.",
};

export default function PrintSettings() {
  const [s, setS] = useState(null);
  const [profile, setProfile] = useState({});
  const [rxProfile, setRxProfile] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [st, me, rx] = await Promise.all([
      api.get("/print-settings"),
      api.get("/profile"),
      api.get("/doctor-rx-profile/me"),
    ]);
    setS(st.data);
    setProfile(me.data || {});
    setRxProfile(rx.data || {});
  };
  useEffect(() => { load(); }, []);

  const update = (patch) => setS((prev) => ({ ...prev, ...patch }));

  const save = async () => {
    setBusy(true);
    try {
      const { id, _id, created_at, updated_at, ...payload } = s || {};
      await api.put("/print-settings", payload);
      toast.success("Settings saved");
      load();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const onUpload = async (e, kind) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fd = new FormData();
    fd.append("file", file);

    try {
      const url = kind === "watermark"
        ? "/print-settings/watermark-upload"
        : "/print-settings/template-upload";

      const { data } = await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });

      if (kind === "watermark") {
        toast.success("Watermark image uploaded");
        update({
          watermark_type: "image",
          watermark_image_url: data.watermark_image_url,
          show_watermark: true,
        });
      } else {
        toast.success("Template uploaded");
        update({ template_image_url: data.template_image_url });
      }
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail));
    } finally {
      if (e.target) e.target.value = "";
    }
  };

  if (!s) return <div className="p-8 text-slate-500">Loading...</div>;

  return (
    <div className="p-6 md:p-8 max-w-[1600px]" data-testid="settings-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "Work Sans" }}>Print Settings</h1>
          <p className="text-slate-500 text-sm mt-1">Configure RX template, paper size, layout positions, and signature.</p>
        </div>
        <Button onClick={save} className="bg-blue-600 hover:bg-blue-700" disabled={busy} data-testid="btn-save-settings">
          <Save size={14} className="mr-2" /> Save Settings
        </Button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="space-y-4">
          <Card className="p-4 border-blue-200 bg-blue-50">
            <div className="text-sm text-blue-900">
              <b>Doctor identity moved.</b> Your header text, license, PTR, signature, header image, and affiliations now live on your personal{" "}
              <a href="/profile" className="underline font-medium">My Profile</a> page so each doctor has their own. This page now only controls clinic-wide print layout.
            </div>
          </Card>

          <Card className="p-5 border-slate-200">
            <h2 className="font-semibold text-slate-900 mb-3" style={{ fontFamily: "Work Sans" }}>Paper & Body</h2>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Paper Size</Label>
                <Select value={s.paper_size || "A4"} onValueChange={(v) => update({ paper_size: v })}>
                  <SelectTrigger data-testid="set-paper-size"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="A4">A4 (210×297 mm)</SelectItem>
                    <SelectItem value="Letter">Letter (8.5×11 in)</SelectItem>
                    <SelectItem value="Half">Half page (5.5×8.5 in)</SelectItem>
                    <SelectItem value="Custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Signature Mode</Label>
                <Select value={s.signature_mode || "final_page_only"} onValueChange={(v) => update({ signature_mode: v })}>
                  <SelectTrigger data-testid="set-signature-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="final_page_only">Final page only</SelectItem>
                    <SelectItem value="every_page">Every page</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {s.paper_size === "Custom" ? (
                <>
                  <NumberField label="Custom Width" value={s.custom_width} onChange={(v) => update({ custom_width: v })} testid="set-custom-w" />
                  <NumberField label="Custom Height" value={s.custom_height} onChange={(v) => update({ custom_height: v })} testid="set-custom-h" />
                </>
              ) : null}
              <NumberField label="Top Margin" value={s.top_margin} onChange={(v) => update({ top_margin: v })} testid="set-top-margin" />
              <NumberField label="Left Margin" value={s.left_margin} onChange={(v) => update({ left_margin: v })} testid="set-left-margin" />
              <NumberField label="Body Start Y" value={s.body_start_y} onChange={(v) => update({ body_start_y: v })} testid="set-body-y" />
              <NumberField label="Body Height" value={s.body_height} onChange={(v) => update({ body_height: v })} testid="set-body-h" />
              <NumberField label="Row Height" value={s.row_height} onChange={(v) => update({ row_height: v })} testid="set-row-h" />
              <NumberField label="Max Rows / Page" value={s.max_rows_per_page} onChange={(v) => update({ max_rows_per_page: v })} suffix="rows" testid="set-max-rows" />
              <NumberField label="Font Size" value={s.font_size} onChange={(v) => update({ font_size: v })} suffix="pt" testid="set-font-size" />
              <NumberField label="RX Body Font" value={s.body_font_size} onChange={(v) => update({ body_font_size: v })} suffix="pt" testid="set-body-font" />
            </div>
            <div className="mt-3 flex items-center gap-3 border-t border-slate-200 pt-3">
              <Switch checked={s.auto_fit !== false} onCheckedChange={(v) => update({ auto_fit: v })} data-testid="set-auto-fit" />
              <div>
                <Label>Auto-fit to any paper size when printing</Label>
                <div className="text-xs text-slate-500">Keeps mm-based layout but lets the browser scale to A4 / Letter / thermal / custom paper automatically.</div>
              </div>
            </div>
          </Card>

          <Card className="p-5 border-slate-200">
            <h2 className="font-semibold text-slate-900 mb-3" style={{ fontFamily: "Work Sans" }}>Field Positions</h2>
            <div className="grid grid-cols-4 gap-3">
              <NumberField label="Patient X" value={s.patient_x} onChange={(v) => update({ patient_x: v })} />
              <NumberField label="Patient Y" value={s.patient_y} onChange={(v) => update({ patient_y: v })} />
              <NumberField label="Date X" value={s.date_x} onChange={(v) => update({ date_x: v })} />
              <NumberField label="Date Y" value={s.date_y} onChange={(v) => update({ date_y: v })} />
              <NumberField label="Age X" value={s.age_x} onChange={(v) => update({ age_x: v })} />
              <NumberField label="Age Y" value={s.age_y} onChange={(v) => update({ age_y: v })} />
              <NumberField label="Gender X" value={s.gender_x} onChange={(v) => update({ gender_x: v })} />
              <NumberField label="Gender Y" value={s.gender_y} onChange={(v) => update({ gender_y: v })} />
              <NumberField label="Signature X" value={s.signature_x} onChange={(v) => update({ signature_x: v })} />
              <NumberField label="Signature Y" value={s.signature_y} onChange={(v) => update({ signature_y: v })} />
            </div>

            <div className="mt-4 flex items-center justify-between gap-4 border-t border-slate-200 pt-4">
              <div className="flex items-center gap-3">
                <Switch checked={!!s.show_doctor_header} onCheckedChange={(v) => update({ show_doctor_header: v })} data-testid="set-show-header" />
                <Label>Show Doctor Header (when no template)</Label>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={!!s.show_watermark} onCheckedChange={(v) => update({ show_watermark: v })} data-testid="set-show-watermark" />
                <Label>Show watermark</Label>
              </div>
            </div>

            <div className="mt-4 border-t border-slate-200 pt-4">
              <h3 className="text-sm font-semibold text-slate-800 mb-3">Prescription Watermark</h3>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div>
                  <Label>Watermark Type</Label>
                  <Select
                    value={s.watermark_type || "text"}
                    onValueChange={(v) => update({ watermark_type: v, show_watermark: true })}
                  >
                    <SelectTrigger data-testid="set-watermark-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="text">Text</SelectItem>
                      <SelectItem value="image">Image</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <NumberField
                  label="Opacity"
                  value={s.watermark_opacity ?? 0.06}
                  onChange={(v) => update({ watermark_opacity: v })}
                  testid="set-watermark-opacity"
                />

                {s.watermark_type === "image" ? (
                  <NumberField
                    label="Image Size"
                    value={s.watermark_image_size ?? 55}
                    onChange={(v) => update({ watermark_image_size: v })}
                    suffix="%"
                    testid="set-watermark-image-size"
                  />
                ) : (
                  <NumberField
                    label="Font Size"
                    value={s.watermark_font_size ?? 72}
                    onChange={(v) => update({ watermark_font_size: v })}
                    suffix="pt"
                    testid="set-watermark-font-size"
                  />
                )}

                {s.watermark_type === "image" ? (
                  <div>
                    <Label>Watermark Image</Label>
                    <div className="mt-1 border border-dashed border-slate-300 rounded-md p-2 bg-slate-50">
                      {s.watermark_image_url ? (
                        <img src={s.watermark_image_url} alt="watermark" className="h-12 mx-auto object-contain mb-2" />
                      ) : (
                        <div className="text-xs text-slate-500 text-center py-2">No image</div>
                      )}
                      <label className="inline-flex w-full justify-center items-center gap-2 text-xs text-blue-700 cursor-pointer">
                        <Upload size={13} /> Upload
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => onUpload(e, "watermark")}
                          data-testid="upload-watermark"
                        />
                      </label>
                      {s.watermark_image_url ? (
                        <button
                          type="button"
                          onClick={() => update({ watermark_image_url: "" })}
                          className="block mx-auto mt-1 text-xs text-red-600 hover:underline"
                        >
                          Remove
                        </button>
                      ) : null}
                    </div>
                  </div>
                ) : (
                  <div>
                    <Label>Rotation</Label>
                    <Input
                      type="number"
                      value={s.watermark_rotation ?? -25}
                      onChange={(e) => update({ watermark_rotation: e.target.value === "" ? null : Number(e.target.value) })}
                      data-testid="set-watermark-rotation"
                    />
                  </div>
                )}
              </div>

              {s.watermark_type !== "image" ? (
                <div className="mt-3">
                  <Label>Watermark Text / Symbol</Label>
                  <Input
                    value={s.watermark_text ?? "Rx"}
                    onChange={(e) => update({ watermark_text: e.target.value })}
                    placeholder="Rx"
                    data-testid="set-watermark-text"
                  />
                </div>
              ) : null}

              <p className="text-xs text-slate-500 mt-2">
                Text watermark supports rotation. Image watermark is placed dead center on the prescription. Recommended opacity: 0.04 to 0.15.
              </p>
            </div>
          </Card>

          <Card className="p-5 border-slate-200">
            <h2 className="font-semibold text-slate-900 mb-3" style={{ fontFamily: "Work Sans" }}>Clinic-wide Template (optional)</h2>
            <p className="text-xs text-slate-500 mb-3">Full-page background image printed underneath every Rx — useful when the clinic uses a pre-printed RX form template.</p>
            <div className="max-w-sm">
              <Label>RX Template Background (full-page)</Label>
              <div className="mt-1 border border-dashed border-slate-300 rounded-md p-3 text-center bg-slate-50">
                {s.template_image_url ? (
                  <img src={s.template_image_url} alt="template" className="max-h-24 mx-auto mb-2" />
                ) : (
                  <div className="text-xs text-slate-500 py-3">No template uploaded</div>
                )}
                <label className="inline-flex items-center gap-2 text-sm text-blue-700 cursor-pointer">
                  <Upload size={14} /> Upload image
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => onUpload(e, "template")} data-testid="upload-template" />
                </label>
                {s.template_image_url ? (
                  <button onClick={() => update({ template_image_url: "" })} className="block mx-auto mt-2 text-xs text-red-600 hover:underline">Remove</button>
                ) : null}
              </div>
            </div>
          </Card>
        </div>

        <div>
          <h2 className="font-semibold text-slate-900 mb-3" style={{ fontFamily: "Work Sans" }}>Preview</h2>
          <div className="bg-slate-100 rounded-md p-4 overflow-auto max-h-[80vh]">
            <div style={{ transform: "scale(0.55)", transformOrigin: "top left", width: "fit-content" }}>
              <PrescriptionPreview
                prescription={{
                  ...SAMPLE_PRESCRIPTION,
                  doctor_snapshot: {
                    name: profile.doctor_name || profile.name,
                    specialty: profile.specialty,
                    license_number: profile.license_number,
                    ptr_number: profile.ptr_number,
                    signature_image_url: profile.signature_image_url,
                    affiliations: profile.affiliations,
                    layout_template: rxProfile.layout_template || "classic",
                    clinics: rxProfile.clinics || [],
                    affiliations_footer: rxProfile.affiliations_footer || "",
                    security_code_enabled: rxProfile.security_code_enabled !== false,
                    follow_up_enabled: rxProfile.follow_up_enabled !== false,
                  },
                }}
                settings={s}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
